import { computed, ref } from 'vue'
import { gql } from 'graphql-tag'
import { useAuthStore } from '@/stores/authStore'

/**
 * THE SERVER CONFIG, on the client — one read per session.
 * ===========================================================================
 * The settings the app needs to know about are declared in the backend's
 * config catalog and marked `public`; this fetches all of them in a single
 * call and keeps them in a module-level singleton.
 *
 * ONE call for all of them, not one per feature (Sandy 2026-08-21): as more
 * rules move into the server config, a composable each would mean a query each
 * on every page load. Adding a setting to the catalog makes it available here
 * with no client change at all.
 *
 * FAILS CLOSED. An unreachable API leaves the map EMPTY, and every reader
 * falls back to its own default — which for a feature flag means "off", the
 * safe end. `loaded` stays false so the next navigation retries.
 *
 * The client comes from `useNuxtApp().$apollo.defaultClient`, NOT from
 * `useApolloClient()`: this is read from route middleware, where there is no
 * component instance for the composable's `inject` to find.
 */

const APP_CONFIG_QUERY = gql`
  query GetAppConfig {
    getAppConfig
  }
`

/** key → stored string. Empty until the first successful load. */
const _values = ref({})
const _loaded = ref(false)

let _inFlight = null

function resolveClient() {
  return useNuxtApp()?.$apollo?.defaultClient ?? null
}

async function _fetch() {
  // One flight per session; concurrent callers await the same promise.
  if (_inFlight) return _inFlight

  _inFlight = (async () => {
    try {
      const client = resolveClient()
      if (!client) return

      const { data } = await client.query({
        query: APP_CONFIG_QUERY,
        fetchPolicy: 'network-only',
      })

      _values.value = data?.getAppConfig ?? {}
      _loaded.value = true
    }
    catch {
      // Unreachable API — readers fall back to their defaults. `_loaded`
      // stays false so the next navigation retries.
    }
    finally {
      _inFlight = null
    }
  })()

  return _inFlight
}

/** Wipe on logout so the next user re-reads the config. */
export function resetServerConfig() {
  _values.value = {}
  _loaded.value = false
  _inFlight = null
}

export function useServerConfig() {
  const authStore = useAuthStore()

  function load() {
    if (_loaded.value || !authStore.isAuthenticated) return

    return _fetch()
  }

  /** Raw string, or the caller's fallback when unset / not loaded. */
  const get = (key, fallback = '') => _values.value[key] ?? fallback

  /** True only for a literal "true" — anything else, including missing. */
  const getBool = (key, fallback = false) => {
    const raw = _values.value[key]

    return raw == null ? fallback : String(raw).trim().toLowerCase() === 'true'
  }

  /** A comma-separated list, cleaned like the server cleans it. */
  const getList = key =>
    [...new Set(get(key).split(',').map(s => s.trim()).filter(Boolean))]

  return {
    values: computed(() => _values.value),
    loaded: computed(() => _loaded.value),
    get,
    getBool,
    getList,
    load,
    reload: () => _fetch(),
  }
}
